import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';

import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useAllInsights } from '@/components/pages/Insights/useInsights';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ColumnVisibilityMenu } from '@/components/ui/table/column-visibility-menu';
import { DataTable } from '@/components/ui/table/data-table';
import { HiddenColumnsIndicator } from '@/components/ui/table/hidden-columns-indicator';
import { useColumnVisibility } from '@/components/ui/table/useColumnVisibility';
import { useBrainLogsInfiniteQuery } from '@/hooks/useBrainLogsInfiniteQuery';
import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import { useModalTryIt, useSessionReplayHref } from '@/hooks/useModalTryIt';
import { usePrefetchSessionContent } from '@/hooks/usePrefetchSessionContent';
import { useSessionKeyboardNav } from '@/hooks/useSessionKeyboardNav';
import DateFilter from '@/modules/analytics/components/filters/DateFilter/DateFilter';
import useFilters from '@/modules/analytics/hooks/useFilters';
import { addLastSeen } from '@/modules/analytics/redux/actions';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { popModal } from '@/redux/modals/actions';
import { selectIsTryItModalOpen } from '@/redux/modals/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import { AnalyticsType, Log, LogsAnalytics } from '@/shared/types/analytics';
import { RootState } from '@/shared/types/state';

import { AllFiltersDropdown } from './AllFiltersDropdown';
import { EditFilterDialog } from './EditFilterDialog';
import { EmptySessions } from './EmptySessions';
import { FiltersBar } from './FiltersBar';
import { createSessionColumns } from './sessionColumns';
import { useFilterViews } from './useFilterViews';
import { useSessionFilters } from './useSessionFilters';

interface SessionsInfiniteTableProps {
  sessionId?: string | null;
}

/**
 * SessionsInfiniteTable component displays brain log sessions in an infinite scrolling table
 */
export const SessionsInfiniteTable: React.FC<SessionsInfiniteTableProps> = ({ sessionId }) => {
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const lastSeen = useSelector((state: RootState) => state.analytics.lastSeen);
  const brainId = useSelector(selectBrainId);
  const { desks } = useDesks();
  const { brains } = useBrains();
  const { languageModelNames } = useLanguageModels();
  const { dateOpen, setDateOpen, filterOpen } = useFilters(AnalyticsType.LOGS);
  const {
    formMethods,
    fields,
    addFilter,
    removeFilter,
    saveFilters,
    activeFilters: filters,
    removeFilterByType,
    loadFilters,
    clearAllFilters,
  } = useSessionFilters();
  // Filter views state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const {
    views: filterViews,
    activeView,
    activeViewId,
    hasUnsavedChanges,
    saveFilterView,
    loadFilterView,
    deleteFilterView,
    setActiveViewId,
    deleteStatus,
  } = useFilterViews(brainId, filters);

  // Handle loading a filter view
  const handleLoadView = useCallback(
    (viewId: string) => {
      const filters = loadFilterView(viewId);
      if (filters) {
        loadFilters(filters);
      }
    },
    [loadFilterView, loadFilters]
  );

  // Handle saving filter view
  const handleSaveView = useCallback(
    async (data: { name: string; overwriteExisting: boolean }) => {
      const overwriteId = data.overwriteExisting && activeViewId ? activeViewId : undefined;
      await saveFilterView(data.name, overwriteId);
    },
    [saveFilterView, activeViewId]
  );

  // Handle updating the active filter view directly
  const handleUpdateActiveView = useCallback(async () => {
    if (activeView) {
      await saveFilterView(activeView.name, activeView.id);
    }
  }, [activeView, saveFilterView]);

  // Handle editing filter view name save
  const handleEditViewSave = useCallback(
    async (name: string, viewId: string) => {
      await saveFilterView(name, viewId);
    },
    [saveFilterView]
  );

  // Handle delete filter view
  const handleDeleteView = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  // Handle delete filter view confirmation
  const handleDeleteViewConfirm = useCallback(async () => {
    if (activeView) {
      await deleteFilterView(activeView.id);
      // Clear filters since the active view is deleted
      clearAllFilters();
    }
    setDeleteConfirmOpen(false);
  }, [activeView, deleteFilterView, clearAllFilters]);

  // Handle clearing all filters
  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    setActiveViewId(undefined);
  }, [clearAllFilters, setActiveViewId]);

  // Handle removing individual filters and clear active view if no filters remain
  const handleRemoveFilter = useCallback(
    (filterType: string) => {
      // Check if this is an insight filter removal
      if (filterType.startsWith('insightFilter-')) {
        const insightId = filterType.replace('insightFilter-', '');
        const insightFilter = filters.find((f) => f.type === 'insightFilters');

        if (insightFilter?.insightFilters) {
          // Check if removing this insight will leave no insights and this is the only filter
          const remainingInsights = insightFilter.insightFilters.filter(
            (insight) => insight.id !== insightId
          );

          // If this is the last insight in the only filter, clear active view
          if (remainingInsights.length === 0 && filters.length === 1) {
            setActiveViewId(undefined);
          }
        }
      } else {
        // For regular filters, check if this is the last filter
        if (filters.length === 1) {
          setActiveViewId(undefined);
        }
      }

      // Remove the filter
      removeFilterByType(filterType);
    },
    [filters, removeFilterByType, setActiveViewId]
  );

  // Fetch custom insight definitions for dynamic columns. Historical values stay visible for
  // any brain that has definitions, regardless of the per-brain insights_enabled toggle.
  const { data: allInsights, isLoading: isLoadingInsights } = useAllInsights(brainId);

  const { handleReplayClick } = useModalTryIt(sessionId || null, { showNavigationHint: true });
  const prefetchSessionContent = usePrefetchSessionContent();

  useEffect(() => {
    if (sessionId && handleReplayClick) {
      handleReplayClick(sessionId);
      // Also update lastSeen state for auto-opened sessions, consistent with manual clicks
      if (lastSeen[lastSeen.length - 1] !== sessionId) {
        dispatch(addLastSeen(sessionId));
      }
    }
  }, [sessionId, handleReplayClick, lastSeen, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(popModal());
    };
  }, [dispatch]);

  const { data, fetchMore, totalCount, hasNextPage, isFetchingNextPage, isLoading } =
    useBrainLogsInfiniteQuery({ limit: 20 });

  const isEmptyState = !isLoading && !isLoadingInsights && data.length === 0;

  const handleRowReplay = useCallback(
    (session: Log) => {
      handleReplayClick(session.session_id, session.start_time);
    },
    [handleReplayClick]
  );

  const columns = useMemo(
    () =>
      createSessionColumns(
        t,
        handleRowReplay,
        desks,
        lastSeen,
        allInsights,
        prefetchSessionContent
      ),
    [t, handleRowReplay, desks, lastSeen, allInsights, prefetchSessionContent]
  );

  // Column visibility management
  const {
    columnVisibility,
    hideableColumns,
    setColumnVisibility,
    resetColumnVisibility,
    hasHiddenColumns,
  } = useColumnVisibility(columns, `brain-logs-${brainId}`);

  // Button ref will be used to prevent the popover modal from closing when you click out
  const customizeButtonRef = useRef<HTMLButtonElement>(null);

  const handleRowClick = useCallback(
    (session: Log) => {
      handleRowReplay(session);
    },
    [handleRowReplay]
  );

  // ?session_id= in a new tab auto-opens the replay modal; left click keeps the onRowClick path
  const getSessionReplayHref = useSessionReplayHref();
  const getRowHref = useCallback(
    (session: Log) => getSessionReplayHref(session.session_id),
    [getSessionReplayHref]
  );

  const isTryItModalOpen = useSelector(selectIsTryItModalOpen);
  useSessionKeyboardNav(data, sessionId, isTryItModalOpen, handleRowReplay, {
    fetchMore,
    hasNextPage,
    isFetchingNextPage,
  });

  const filterIndexByType = useMemo(() => {
    return fields.reduce(
      (acc, field, index) => {
        const fieldType = (field as LogsAnalytics | undefined)?.type;
        if (fieldType) {
          acc[fieldType] = index;
        }
        return acc;
      },
      {} as Record<string, number>
    );
  }, [fields]);

  return (
    <FormProvider {...formMethods}>
      <div className="flex h-full min-h-0 flex-col">
        <DataTable.Root<Log, unknown>
          fill
          className="min-h-0 flex-1"
          columns={columns}
          data={data}
          fetchNextPage={fetchMore}
          hasNextPage={hasNextPage}
          rowCount={totalCount}
          onRowClick={handleRowClick}
          getRowHref={getRowHref}
          resourceName="session"
          isFetchingNextPage={isFetchingNextPage}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          isLoading={isLoading || isLoadingInsights}
          pagination={true}
        >
          <DataTable.Toolbar>
            {(_table) => (
              <div className="mb-4 flex items-center justify-between">
                <DateFilter dateOpen={dateOpen} setDateOpen={setDateOpen} disabled={filterOpen} />
                <div className="flex items-center gap-2">
                  <AllFiltersDropdown
                    savedViews={filterViews}
                    onLoadView={handleLoadView}
                    formMethods={formMethods}
                    fields={fields}
                    addFilter={addFilter}
                    removeFilter={removeFilter}
                    saveFilters={saveFilters}
                    activeFilters={filters}
                  />
                  <ColumnVisibilityMenu
                    columns={hideableColumns}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={setColumnVisibility}
                    onReset={resetColumnVisibility}
                    open={isCustomizeOpen}
                    onOpenChange={setIsCustomizeOpen}
                    onInteractOutside={(e) => {
                      if (e.target === customizeButtonRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </DataTable.Toolbar>
          <FiltersBar
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
            desks={desks}
            brains={brains}
            languageModelNames={languageModelNames}
            onSaveView={handleSaveView}
            onUpdateActiveView={handleUpdateActiveView}
            onDeleteView={handleDeleteView}
            onSelectView={handleLoadView}
            activeView={activeView}
            existingViews={filterViews}
            activeViewId={activeViewId}
            hasUnsavedChanges={hasUnsavedChanges}
            filterIndexByType={filterIndexByType}
            removeFilterAtIndex={removeFilter}
            onSaveFilters={saveFilters}
            onClearAll={handleClearFilters}
            insights={allInsights}
          />
          {isEmptyState ? <EmptySessions variant="full" /> : <DataTable.InfiniteContent />}
        </DataTable.Root>

        {/* Footer: Total count + Hidden columns indicator */}
        {(Number(totalCount) > 0 || hasHiddenColumns) && (
          <div className="mt-4 grid h-10 grid-cols-3 items-center">
            <div className="flex items-center text-sm text-foreground-muted">
              {Number(totalCount) > 0 &&
                t('analytics.total_sessions', { count: Number(totalCount) })}
            </div>
            <div className="flex justify-center">
              {hasHiddenColumns && (
                <HiddenColumnsIndicator
                  onCustomize={() => setIsCustomizeOpen((prev) => !prev)}
                  buttonRef={customizeButtonRef}
                />
              )}
            </div>
            <div />
          </div>
        )}

        {/* Edit Filter Dialog */}
        <EditFilterDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSave={handleEditViewSave}
          activeView={activeView}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={deleteConfirmOpen}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteConfirmOpen(false);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('brain_logs.delete_filter_view_title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('brain_logs.delete_filter_view_description', { 0: activeView?.name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <Button
                onClick={handleDeleteViewConfirm}
                variant="destructive"
                isLoading={deleteStatus === 'pending'}
              >
                {t('common.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </FormProvider>
  );
};
